import type { PageResponseDto, SortDirection } from '@/lib/api/event/types';
import { createHttpClient } from '@/lib/api/httpClient';
import { resolveStrapiBaseUrl } from './utils';

const BASE_URL = resolveStrapiBaseUrl();

const http = createHttpClient({ baseUrl: BASE_URL });

interface LeadingOutcomeDto {
  q: string;
  c: string;
  w?: boolean;
}

interface EventSearchItemDto {
  i: string;
  s: string;
  ti: string;
  lo: LeadingOutcomeDto | null;
  oc: number;
}

export interface EventSearchLeadingOutcome {
  question: string;
  chance: number;
  winner: boolean;
}

export interface EventSearchItem {
  id: string;
  slug: string;
  title: string;
  leadingOutcome: EventSearchLeadingOutcome | null;
  outcomesCount: number;
}

export type EventsProxySortBy =
  | 'VOLUME_24H'
  | 'TOTAL_VOLUME'
  | 'LIQUIDITY'
  | 'NEWEST'
  | 'OLDEST'
  | 'ENDING_SOON'
  | 'CHANCE';

export interface SearchEventsProxyParams {
  q?: string;
  categories?: string[];
  tags?: string[];
  sortBy?: EventsProxySortBy;
  sortDirection?: SortDirection;
  endingPeriod?: string;
  featured?: boolean;
  locale?: string;
  page?: number;
  size?: number;
}

export interface SearchEventsProxyResult {
  events: EventSearchItem[];
  total: number;
  page: number;
  size: number;
  totalPages: number;
}

function toFiniteNumber(value: string | number | null | undefined): number {
  if (value == null) return 0;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
}

function mapLeadingOutcome(dto: LeadingOutcomeDto | null): EventSearchLeadingOutcome | null {
  if (!dto) return null;
  return { question: dto.q, chance: toFiniteNumber(dto.c), winner: dto.w === true };
}

function mapEventSearchItem(dto: EventSearchItemDto): EventSearchItem {
  return {
    id: dto.i,
    slug: dto.s,
    title: dto.ti,
    leadingOutcome: mapLeadingOutcome(dto.lo),
    outcomesCount: dto.oc,
  };
}

export async function searchEventsProxy(
  params: SearchEventsProxyParams,
): Promise<SearchEventsProxyResult> {
  const dto = await http.get<PageResponseDto<EventSearchItemDto>>('/api/events', {
    query: {
      ...(params.q ? { q: params.q } : {}),
      ...(params.categories?.length ? { categories: params.categories } : {}),
      ...(params.tags?.length ? { tags: params.tags } : {}),
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.sortDirection ? { sortDirection: params.sortDirection } : {}),
      ...(params.endingPeriod ? { endingPeriod: params.endingPeriod } : {}),
      ...(params.featured !== undefined ? { featured: params.featured } : {}),
      ...(params.locale ? { locale: params.locale } : {}),
      ...(params.page !== undefined ? { page: params.page } : {}),
      ...(params.size !== undefined ? { size: params.size } : {}),
    },
  });

  return {
    events: (dto.content ?? []).map(mapEventSearchItem),
    total: dto.totalElements ?? 0,
    page: (dto.number ?? 0) + 1,
    size: dto.size ?? 0,
    totalPages: dto.totalPages ?? 0,
  };
}
